import { useTranslation } from 'react-i18next';
/**
 * Type Properties (BIM "Edit Type") dialog.
 *
 * Category-driven parameter table for the selected element's family type, with
 * Duplicate / Rename management, search, live preview, Edit Assembly for
 * layered types, and a type switcher. Applying edits propagates to every
 * instance of the type as one undoable command.
 */

import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    HStack,
    Input,
    Select,
    Text,
    VStack,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { familyManager } from '../../core/FamilyManager';
import {
    TypeRef,
    commitTypeEdit,
    countInstancesOfType,
    duplicateTypeInFamily,
    ensureSymbolForElement,
    getEditableParameters,
    renameTypeInProject,
    normalizeSymbolParameters,
} from '../../core/typePropertiesService';
import {
    AssemblyLayerRow,
    TypeParameterDef,
    getTypeSchema,
    resolveTypeParameterRows,
} from '../../config/typeParameterSchemas';
import { TypeParameterRow } from './TypeParameterRow';
import { EditAssemblyDialog } from './EditAssemblyDialog';
import { TypePreviewPanel } from './TypePreviewPanel';

/** Dispatch a toast notification via the global NotificationHost. */
function notify(message: string, status: 'info' | 'success' | 'warning' | 'error' = 'info') {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message, status } }));
}

type NameDialogMode = 'duplicate' | 'rename' | null;

export function TypePropertiesDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.manageDialog === 'typeProperties');
    const target = useAppStore((s) => s.typePropertiesTarget);
    const closeManageDialog = useAppStore((s) => s.closeManageDialog);
    const project = useBIMStore((s) =>s.project); 
// Dialog-local editing state (draft is discarded on close/Cancel).
    const [ref, setRef] = useState
<TypeRef | null>(null); const [original, setOriginal] = useState<TypeRef | null>(null); const [draft, setDraft] = useState<Record<string, any>>({});
    const [search, setSearch] = useState('');
    const [assemblyOpen, setAssemblyOpen] = useState(false);
    const [nameMode, setNameMode] = useState<NameDialogMode>(null);
    const [nameDraft, setNameDraft] = useState('');

    const category = target?.category ?? 'Generic Models';
    const schema = useMemo(() => getTypeSchema(category), [category]);
    const formulaTargets = useMemo(
        () => (ref ? familyManager.getFormulaTargets(ref.family) : new Set<string>()),
        [ref],
    );

    // (Re)initialize the draft whenever the dialog opens for a target.
    useEffect(() => {
        if (!isOpen || !target) return;
        setSearch('');
        const firstId = target.elementIds[0];
        const element = firstId ? project.elements.get(firstId) : undefined;
        let initRef: TypeRef = { family: target.family, typeName: target.type };
        let parameters: Record<string, any> = {};

        if (element) {
            initRef = ensureSymbolForElement(element);
            const editable = getEditableParameters(element);
            parameters = editable?.parameters ?? {};
        } else {
            const symbol = familyManager.getType(initRef.family, initRef.typeName);
            if (symbol) parameters = normalizeSymbolParameters(JSON.parse(JSON.stringify(symbol.parameters)));
        }
        setRef(initRef);
        setOriginal(initRef);
        setDraft(parameters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, target]);

    // Type dropdown: switching loads the destination symbol's draft.
    const typeNames = useMemo(() => (ref ? familyManager.getTypeNames(ref.family) : []), [ref]);

    const switchType = (typeName: string) => {
        if (!ref || typeName === ref.typeName) return;
        const symbol = familyManager.getType(ref.family, typeName);
        setRef({ ...ref, typeName });
        setDraft(symbol ? normalizeSymbolParameters(JSON.parse(JSON.stringify(symbol.parameters))) : {});
    };

    const setParam = (name: string, value: any) => {
        setDraft(prev => ({ ...prev, [name]: value }));
    };

    const rows = useMemo(
        () => resolveTypeParameterRows(category, draft),
        [category, draft],
    );

    const term = search.trim().toLowerCase();
    const filteredRows = useMemo(() => {
        if (!term) return rows;
        return rows
            .map(group => ({
                ...group,
                defs: group.defs.filter(d => d.label.toLowerCase().includes(term) || d.name.toLowerCase().includes(term)),
            }))
            .filter(group => group.defs.length > 0);
    }, [rows, term]);

    const isDirty = !!original && !!ref && (
        JSON.stringify(draft) !== JSON.stringify(
            (familyManager.getType(original.family, original.typeName)?.parameters) ?? {}
        ) || original.typeName !== ref.typeName
    );

    const instanceCount = useMemo(
        () => (ref ? countInstancesOfType(ref) : 0),
        [ref, project.elements, draft],
    );

    const handleApply = () => {
        if (!ref || !target) return;
        const finalParams = familyManager.applyFormulas(ref.family, draft);
        const switched = original ? original.typeName !== ref.typeName : false;
        commitTypeEdit({
            ref,
            newParams: finalParams,
            switchElementIds: switched ? target.elementIds : undefined,
        });
        setDraft(finalParams);
        const count = switched ? target.elementIds.length : countInstancesOfType(ref);
        notify(
            switched
                ? `Type switched to "${ref.typeName}" on ${count} element${count === 1 ? '' : 's'}.`
                : `Type "${ref.typeName}" updated${count > 0 ? ` — ${count} instance${count === 1 ? '' : 's'} affected` : ''}.`,
            'success',
        );
        // Keep the dialog open like BIM only after Apply; here the footer
        // button IS Apply+Close, mirroring this app's modal convention.
        closeManageDialog();
    };

    const handleNameSubmit = () => {
        if (!ref || !target) return;
        const trimmed = nameDraft.trim();
        if (!trimmed) { setNameMode(null); return; }

        if (nameMode === 'rename') {
            const ok = renameTypeInProject(ref, trimmed);
            if (!ok) {
                notify(`A type named "${trimmed}" already exists in ${ref.family}.`, 'error');
            } else {
                setRef({ ...ref, typeName: trimmed });
                setOriginal({ ...ref, typeName: trimmed });
                notify(`Type renamed to "${trimmed}".`, 'success');
            }
        } else if (nameMode === 'duplicate') {
            // Commit the current draft first so the copy carries live edits.
            const finalParams = familyManager.applyFormulas(ref.family, draft);
            familyManager.setTypeParameters(ref.family, ref.typeName, finalParams);
            const newName = duplicateTypeInFamily(ref, trimmed);
            if (!newName) {
                notify('Duplicate failed: source type missing.', 'error');
            } else {
                const newRef: TypeRef = { family: ref.family, typeName: newName };
                setDraft(finalParams);
                setRef(newRef);
                setOriginal(newRef);
                // Freshly duplicated type is not yet used by any instance;
                // point the original selection at it (BIM auto-switches).
                commitTypeEdit({ ref: newRef, newParams: finalParams, switchElementIds: target.elementIds });
                notify(`Type duplicated as "${newName}" and applied to selection.`, 'success');
            }
        }
        setNameMode(null);
    };

    const assemblyLayers = (): AssemblyLayerRow[] => {
        const raw = draft['layers'] ?? draft['Layers'];
        if (Array.isArray(raw) && raw.length > 0) {
            return raw.map((l: any) => ({
                function: String(l.function ?? 'Structure'),
                materialId: String(l.materialId ?? 'Generic'),
                thickness: typeof l.thickness === 'number' ? l.thickness : 0.2,
                wraps: Boolean(l.wraps),
            }));
        }
        return [{ function: 'Structure', materialId: 'Generic', thickness: typeof draft['thickness'] === 'number' ? draft['thickness'] : 0.2, wraps: false }];
    };

    const onAssemblyApply = (layers: AssemblyLayerRow[]) => {
        const total = layers.reduce((sum, l) => sum + (l.thickness || 0), 0);
        setDraft(prev => ({ ...prev, layers, thickness: total }));
    };

    if (!isOpen || !target || !ref) return null;

    return (
        <>
            <AppDialog
                isOpen={isOpen}
                onClose={closeManageDialog}
                size="xl"
                scrollBehavior="inside"
                title={t('misc.typeProperties')}
                footer={
                    <>
                        <DialogButton variant="ghost" onClick={closeManageDialog}>{t('misc.cancel')}</DialogButton>
                        <DialogButton variant="primary" onClick={handleApply}>{t('misc.oK')}</DialogButton>
                    </>
                }
            >
                        <VStack spacing={3} align="stretch" flex="1" minH={0} h="100%">
                            {/* Family / Type management row — fixed, does not scroll */}
                            <VStack spacing={2} align="stretch" flexShrink={0}>
                                    <HStack spacing={3}>
                                        <Text fontSize="xs" color="var(--color-text-secondary)" w="52px" flexShrink={0}>{t('misc.family')}</Text>
                                        <Box
                                            fontSize="sm"
                                            flex="1"
                                            px={2}
                                            py={1}
                                            minW={0}
                                            borderRadius="var(--cad-radius-sm)"
                                            bg="var(--color-bg-primary)"
                                            border="1px solid var(--color-border)"
                                            color="var(--color-text-secondary)"
                                            whiteSpace="nowrap"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                        >
                                            {ref.family}
                                        </Box>
                                    </HStack>
                                    <HStack spacing={3}>
                                        <Text fontSize="xs" color="var(--color-text-secondary)" w="52px" flexShrink={0}>{t('misc.type')}</Text>
                                        <Select
                                            size="sm"
                                            flex="1"
                                            value={ref.typeName}
                                            onChange={(e) => switchType(e.target.value)}
                                            bg="var(--color-bg-primary)"
                                            borderColor="var(--color-border)"
                                            title={t('misc.selectTheTypeToEditOrAssignToTheSelection')}
                                        >
                                            {typeNames.map(t => <option key={t} value={t}>{t}</option>)}
                                        </Select>
                                        <HStack spacing={1} flexShrink={0}>
                                            <Button size="xs" variant="outline" onClick={() => { setNameDraft(`${ref.typeName} Copy`); setNameMode('duplicate'); }}>{t('misc.duplicate')}</Button>
                                            <Button size="xs" variant="outline" onClick={() => { setNameDraft(ref.typeName); setNameMode('rename'); }}>{t('misc.rename')}</Button>
                                        </HStack>
                                    </HStack>
                                    <HStack spacing={2}>
                                        <Search size={12} />
                                        <Input
                                            size="xs"
                                            placeholder={t('misc.searchParameters')}
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            bg="var(--color-bg-primary)"
                                            borderColor="var(--color-border)"
                                        />
                                    </HStack>
                                </VStack>

                                {/* Parameter groups table — the only region
                                    that scrolls; spans the full dialog width. */}
                                <Box
                                    flex="1"
                                    minH={0}
                                    overflowY="auto"
                                    border="1px solid var(--color-border)"
                                    borderRadius="var(--cad-radius-sm)"
                                >
                                    {filteredRows.length === 0 ? (
                                        <Text fontSize="xs" color="var(--color-text-secondary)" p={4} textAlign="center">
                                            No parameters match “{search}”.
                                        </Text>
                                    ) : (
                                        filteredRows.map(group => (
                                            <Box key={group.group}>
                                                <HStack
                                                    px={2}
                                                    py={1.5}
                                                    bg="var(--color-bg-tertiary)"
                                                    position="sticky"
                                                    top={0}
                                                    zIndex={1}
                                                    borderBottom="1px solid var(--color-border)"
                                                    justify="space-between"
                                                >
                                                    <Text fontSize="xs" fontWeight="semibold">
                                                        {group.group}
                                                    </Text>
                                                    {group.group === 'Construction' && schema.hasAssembly && (
                                                        <Button
                                                            size="xs"
                                                            variant="outline"
                                                            leftIcon={<SlidersHorizontal size={11} />}
                                                            onClick={() => setAssemblyOpen(true)}
                                                        >{t('misc.editAssembly')}</Button>
                                                    )}
                                                </HStack>
                                                {group.defs.map((def: TypeParameterDef) => (
                                                    <HStack key={def.name} px={2} py={1} spacing={3} borderBottom="1px solid var(--color-border)" _last={{ borderBottomWidth: 0 }}>
                                                        <Text fontSize="xs" color="var(--color-text-secondary)" w="46%" minW="140px" flexShrink={0}>
                                                            {def.label}
                                                            {def.unit && <Box as="span" color="var(--color-text-secondary)" opacity={0.7}> ({def.unit})</Box>}
                                                        </Text>
                                                        <Box flex="1" minW={0}>
                                                            <TypeParameterRow
                                                                def={def}
                                                                value={draft[def.name] ?? group.values[def.name]}
                                                                readOnly={formulaTargets.has(def.name)}
                                                                onChange={(next) => setParam(def.name, next)}
                                                            />
                                                        </Box>
                                                    </HStack>
                                                ))}
                                            </Box>
                                        ))
                                    )}
                                </Box>

                                <HStack justify="space-between">
                                    <Text fontSize="xs" color="var(--color-text-secondary)">
                                        {instanceCount} instance{instanceCount === 1 ? '' : 's'} of this type in the project
                                    </Text>
                                    {isDirty && (
                                        <Text fontSize="xs" color="var(--color-accent)">{t('misc.unappliedChanges')}</Text>
                                    )}
                                </HStack>

                                {/* Preview strip — bottom of the single column,
                                    full dialog width; the figure letterboxes
                                    inside and never clips. */}
                                <Box flexShrink={0}>
                                    <TypePreviewPanel category={category} parameters={draft} />
                                </Box>
                        </VStack>
            </AppDialog>

            {schema.hasAssembly && (
                <EditAssemblyDialog
                    isOpen={assemblyOpen}
                    onClose={() => setAssemblyOpen(false)}
                    layerStation={draft['layerStation'] ?? 'Finish 1 [Face Exterior]'}
                    structure={draft['function'] ?? 'Exterior'}
                    layers={assemblyLayers()}
                    onApply={onAssemblyApply}
                />
            )}

            {/* Duplicate / Rename name prompt */}
            <AppDialog
                isOpen={nameMode !== null}
                onClose={() => setNameMode(null)}
                size="sm"
                title={nameMode === 'rename' ? 'Rename Type' : 'Duplicate Type'}
                footer={
                    <>
                        <DialogButton variant="ghost" onClick={() => setNameMode(null)}>{t('misc.cancel')}</DialogButton>
                        <DialogButton
                            variant="primary"
                            isDisabled={!nameDraft.trim()}
                            onClick={handleNameSubmit}
                        >{t('misc.oK')}</DialogButton>
                    </>
                }
            >
                <Input
                    size="sm"
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNameSubmit(); }}
                    bg="var(--color-bg-primary)"
                    borderColor="var(--color-border)"
                />
            </AppDialog>
        </>
    );
}
